import { useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { X } from "lucide-react";
import { amount, dateLabel, drcr, fyStartOf, money, todayISO, useBooks } from "@/lib/books";
import { Input } from "@/components/ui/input";
import { AsOfFilter, DataTable, ExportMenu, Loaded, PageHeader, Pager, PeriodFilter } from "@/components/books/ui";
import { LedgerStatement, useFyMonth, usePeriod } from "@/components/books/Statement";
import { HeadRow, StatementTable, TotalRow, TreeRows } from "@/components/books/StatementTree";
import { REPORTS } from "@/pages/books/Reports";

// ---------------------------------------------------------------- shell
function ReportShell({ report, filters, exportPath, exportParams, children }) {
  const navigate = useNavigate();
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3 print:hidden">
        <div>
          <div className="text-xs font-medium text-slate-500">{report.category}</div>
          <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">{report.name}</h1>
        </div>
        <div className="flex items-center gap-2">
          {exportPath && <ExportMenu path={exportPath} params={exportParams} />}
          <button onClick={() => navigate("/reports")} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-rose-600" title="Close report">
            <X size={18} />
          </button>
        </div>
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2 print:hidden">
        <span className="text-sm text-slate-500">Filters:</span>
        {filters}
      </div>
      {children}
    </div>
  );
}

function useAsOf() {
  const [params, setParams] = useSearchParams();
  const value = params.get("as_of") || todayISO();
  const set = (v) => {
    const next = new URLSearchParams(params);
    next.set("as_of", v);
    setParams(next, { replace: true });
  };
  return [value, set];
}

const periodLabel = (p) => `From ${dateLabel(p.from)} To ${dateLabel(p.to)}`;
const linkFor = (p) => `?from=${p.from}&to=${p.to}`;

// ---------------------------------------------------------------- profit and loss
function stockNote(stock) {
  if (!stock) return null;
  if (stock.source === "missing") return "not yet reported by Tally";
  if (stock.source === "tally" && !stock.exact) return `as valued by Tally on ${dateLabel(stock.as_of)}`;
  return null;
}

function ProfitAndLoss({ report, horizontal }) {
  const [period, setPeriod, fyMonth] = usePeriod("this_fy");
  const params = { date_from: period.from, date_to: period.to };
  const state = useBooks("/books/reports/profit-and-loss", params);
  const lp = linkFor(period);
  return (
    <ReportShell report={report} exportPath="/books/reports/profit-and-loss" exportParams={params}
      filters={<><PeriodFilter value={period} onChange={setPeriod} fyMonth={fyMonth} /><span className="text-xs text-slate-500">Basis: Accrual</span></>}>
      <Loaded state={state}>
        {state.data && (horizontal ? <PLHorizontal d={state.data} period={period} lp={lp} /> : <PLVertical d={state.data} period={period} lp={lp} />)}
      </Loaded>
    </ReportShell>
  );
}

function children(section) {
  return section?.children || [];
}

function PLVertical({ d, period, lp }) {
  const s = d.sections;
  const note = stockNote(d.closing_stock);
  const opIncome = s["Sales Accounts"].amount + s["Direct Incomes"].amount;
  return (
    <StatementTable company={d.company} title="Profit and Loss" subtitle={<>Basis: Accrual<br />{periodLabel(period)}</>}>
      <HeadRow label="Operating Income" />
      {[...children(s["Sales Accounts"]), ...children(s["Direct Incomes"])].map((n) => <TreeRows key={n.guid || n.name} node={n} depth={1} linkParams={lp} />)}
      <TotalRow label="Total for Operating Income" value={opIncome} />
      <HeadRow label="Cost of Goods Sold" />
      <tr><td className="py-1.5 pl-[34px] pr-4">Opening Stock</td><td className="py-1.5 pr-4 text-right tabular-nums">{amount(d.opening_stock.value)}</td></tr>
      {[...children(s["Purchase Accounts"]), ...children(s["Direct Expenses"])].map((n) => <TreeRows key={n.guid || n.name} node={n} depth={1} linkParams={lp} />)}
      <tr>
        <td className="py-1.5 pl-[34px] pr-4">Less: Closing Stock{note && <span className="ml-2 text-xs text-amber-700">({note})</span>}</td>
        <td className="py-1.5 pr-4 text-right tabular-nums">({amount(d.closing_stock.value)})</td>
      </tr>
      <TotalRow label="Total for Cost of Goods Sold" value={d.cost_of_goods_sold} />
      <TotalRow label={d.gross_profit >= 0 ? "Gross Profit" : "Gross Loss"} value={d.gross_profit} />
      <HeadRow label="Operating Expense" />
      {children(s["Indirect Expenses"]).map((n) => <TreeRows key={n.guid || n.name} node={n} depth={1} linkParams={lp} />)}
      <TotalRow label="Total for Operating Expense" value={s["Indirect Expenses"].amount} />
      <TotalRow label="Operating Profit" value={d.operating_profit} />
      <HeadRow label="Non Operating Income" />
      {children(s["Indirect Incomes"]).map((n) => <TreeRows key={n.guid || n.name} node={n} depth={1} linkParams={lp} />)}
      <TotalRow label="Total for Non Operating Income" value={s["Indirect Incomes"].amount} />
      <TotalRow label={d.net_profit >= 0 ? "Net Profit" : "Net Loss"} value={d.net_profit} />
    </StatementTable>
  );
}

/** Two sides of a T-account, each a list of [label, amount, node?] rows. */
/** One side of a T-format statement. Zero rows are left out, as Tally does.
 * Both sides sit in one grid row, so their totals line up at the bottom. */
function TSide({ title, rows, total, totalLabel = "Total", lp }) {
  const shown = rows.filter(([, value, node]) => Math.abs(value) >= 0.005 || node?.children?.length);
  return (
    <div className="flex flex-col">
      {title && <div className="border-b border-slate-200 px-4 py-2.5 text-base font-semibold italic">{title}</div>}
      <div className="flex-1 py-1">
        <table className="w-full text-sm">
          <tbody>
            {shown.map(([label, value, node], i) => node ? (
              <TreeRows key={i} node={{ ...node, name: label }} depth={0} linkParams={lp} openDepth={0} />
            ) : (
              <tr key={i}>
                <td className="py-1.5 pl-4 pr-4 font-medium">{label}</td>
                <td className="py-1.5 pr-4 text-right tabular-nums">{amount(value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className={`flex justify-between border-t border-slate-300 px-4 py-2.5 font-semibold ${totalLabel ? "italic" : ""}`}>
        <span>{totalLabel}</span><span className="tabular-nums">{amount(total)}</span>
      </div>
    </div>
  );
}

/** Tally's own layout: the trading account (to gross profit) above, its
 * total on both sides, then the profit & loss account below. */
function PLHorizontal({ d, period, lp }) {
  const s = d.sections;
  const gp = d.gross_profit;
  const np = d.net_profit;
  const tradingDr = d.opening_stock.value + s["Purchase Accounts"].amount + s["Direct Expenses"].amount + Math.max(gp, 0);
  const plDr = s["Indirect Expenses"].amount + Math.max(np, 0) + Math.max(-gp, 0);
  const group = (name) => [name, s[name].amount, s[name]];
  const tradingLeft = [
    ["Opening Stock", d.opening_stock.value], group("Purchase Accounts"), group("Direct Expenses"),
    ...(gp >= 0 ? [["Gross Profit c/o", gp]] : []),
  ];
  const tradingRight = [
    group("Sales Accounts"), group("Direct Incomes"), ["Closing Stock", d.closing_stock.value],
    ...(gp < 0 ? [["Gross Loss c/o", -gp]] : []),
  ];
  const plLeft = [
    ...(gp < 0 ? [["Gross Loss b/f", -gp]] : []), group("Indirect Expenses"),
    ...(np >= 0 ? [["Net Profit", np]] : []),
  ];
  const plRight = [
    ...(gp >= 0 ? [["Gross Profit b/f", gp]] : []), group("Indirect Incomes"),
    ...(np < 0 ? [["Net Loss", -np]] : []),
  ];
  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <div className="border-b border-slate-100 px-6 py-5 text-center">
        <div className="text-sm text-slate-500">{d.company}</div>
        <div className="text-xl font-semibold">Horizontal Profit and Loss</div>
        <div className="mt-1 text-sm text-slate-600">{periodLabel(period)} · Basis: Accrual</div>
      </div>
      <div className="grid md:grid-cols-2 md:divide-x md:divide-slate-200">
        <TSide title="Expense" rows={tradingLeft} total={tradingDr} totalLabel="" lp={lp} />
        <TSide title="Income" rows={tradingRight} total={tradingDr} totalLabel="" lp={lp} />
      </div>
      <div className="grid md:grid-cols-2 md:divide-x md:divide-slate-200">
        <TSide rows={plLeft} total={plDr} lp={lp} />
        <TSide rows={plRight} total={plDr} lp={lp} />
      </div>
      <div className="border-t border-slate-100 px-6 py-3 text-xs text-slate-500">Amounts in INR, as Tally's Profit & Loss A/c: trading account above, profit & loss below.</div>
    </div>
  );
}

// ---------------------------------------------------------------- balance sheet
const LIABILITY_SECTIONS = ["Capital Account", "Loans (Liability)", "Current Liabilities", "Suspense A/c", "Branch / Divisions"];
const ASSET_SECTIONS = ["Fixed Assets", "Investments", "Current Assets", "Misc. Expenses (ASSET)"];

function BalanceSheet({ report, horizontal }) {
  const [asOf, setAsOf] = useAsOf();
  const fyMonth = useFyMonth();
  const lp = `?from=${fyStartOf(asOf, fyMonth)}&to=${asOf}`;
  const state = useBooks("/books/reports/balance-sheet", { as_of: asOf });
  return (
    <ReportShell report={report} exportPath="/books/reports/balance-sheet" exportParams={{ as_of: asOf }}
      filters={<><AsOfFilter value={asOf} onChange={setAsOf} /><span className="text-xs text-slate-500">Basis: Accrual</span></>}>
      <Loaded state={state}>
        {state.data && (horizontal ? <BSHorizontal d={state.data} lp={lp} /> : <BSVertical d={state.data} lp={lp} />)}
      </Loaded>
    </ReportShell>
  );
}

function sectionBlock(s, name, lp, extra) {
  const node = s[name];
  if (!node || (!node.amount && !node.children.length && !extra)) return null;
  return (
    <>
      <tr><td className="py-1.5 pl-[34px] pr-4 font-semibold text-slate-800" colSpan={2}>{name}</td></tr>
      {node.children.map((n) => <TreeRows key={n.guid || n.name} node={n} depth={2} linkParams={lp} />)}
      {extra}
      <tr>
        <td className="py-1.5 pl-[34px] pr-4 text-slate-600">Total for {name}</td>
        <td className="border-t border-slate-200 py-1.5 pr-4 text-right font-medium tabular-nums">{amount(node.amount + (extra ? extra.props.value || 0 : 0))}</td>
      </tr>
    </>
  );
}

function StockRow({ value, note }) {
  return (
    <tr>
      <td className="py-1.5 pr-4" style={{ paddingLeft: 52 }}>Stock-in-Hand (closing stock){note && <span className="ml-2 text-xs text-amber-700">({note})</span>}</td>
      <td className="py-1.5 pr-4 text-right tabular-nums">{amount(value)}</td>
    </tr>
  );
}

function BSVertical({ d, lp }) {
  const s = d.sections;
  const pl = d.profit_and_loss;
  const stock = d.stock.value || 0;
  const liabilities = LIABILITY_SECTIONS.filter((n) => n !== "Capital Account").reduce((t, n) => t + s[n].amount, 0);
  return (
    <StatementTable company={d.company} title="Balance Sheet" subtitle={<>Basis: Accrual<br />As of {dateLabel(d.as_of)}</>}>
      <HeadRow label="Assets" />
      {ASSET_SECTIONS.map((n) => (
        <SectionFragment key={n}>
          {sectionBlock(s, n, lp, n === "Current Assets" && stock ? <StockRow value={stock} note={stockNote(d.stock)} /> : null)}
        </SectionFragment>
      ))}
      <TotalRow label="Total for Assets" value={d.total_assets} />
      <HeadRow label="Liabilities & Equities" />
      {LIABILITY_SECTIONS.filter((n) => n !== "Capital Account").map((n) => <SectionFragment key={n}>{sectionBlock(s, n, lp)}</SectionFragment>)}
      <TotalRow label="Total for Liabilities" value={liabilities} strong={false} muted />
      <tr><td className="py-1.5 pl-[34px] pr-4 font-semibold text-slate-800" colSpan={2}>Equities</td></tr>
      {s["Capital Account"].children.map((n) => <TreeRows key={n.guid || n.name} node={n} depth={2} linkParams={lp} />)}
      <tr><td className="py-1.5 pr-4" style={{ paddingLeft: 52 }}>Profit & Loss A/c — opening balance</td><td className="py-1.5 pr-4 text-right tabular-nums">{amount(pl.opening + pl.previous_years)}</td></tr>
      <tr><td className="py-1.5 pr-4" style={{ paddingLeft: 52 }}>Profit & Loss A/c — current period</td><td className="py-1.5 pr-4 text-right tabular-nums">{amount(pl.current_period)}</td></tr>
      <tr>
        <td className="py-1.5 pl-[34px] pr-4 text-slate-600">Total for Equities</td>
        <td className="border-t border-slate-200 py-1.5 pr-4 text-right font-medium tabular-nums">{amount(s["Capital Account"].amount + pl.total)}</td>
      </tr>
      <TotalRow label="Total for Liabilities & Equities" value={d.total_liabilities} />
      {Math.abs(d.difference) >= 0.01 && (
        <tr><td className="px-4 py-2 text-amber-800">Difference in opening balances</td><td className="px-4 py-2 text-right tabular-nums text-amber-800">{amount(d.difference)}</td></tr>
      )}
    </StatementTable>
  );
}

const SectionFragment = ({ children }) => <>{children}</>;

function BSHorizontal({ d, lp }) {
  const s = d.sections;
  const pl = d.profit_and_loss;
  const left = [
    ...LIABILITY_SECTIONS.filter((n) => s[n].amount || s[n].children.length).map((n) => [n, s[n].amount, s[n]]),
    ["Profit & Loss A/c", pl.total],
    ...(d.difference < -0.005 ? [["Difference in opening balances", -d.difference]] : []),
  ];
  const right = [
    ...ASSET_SECTIONS.filter((n) => s[n].amount || s[n].children.length).map((n) => [n, s[n].amount, s[n]]),
    ["Closing Stock", d.stock.value || 0],
    ...(d.difference > 0.005 ? [["Difference in opening balances", d.difference]] : []),
  ];
  const total = Math.max(d.total_assets, d.total_liabilities);
  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <div className="border-b border-slate-100 px-6 py-5 text-center">
        <div className="text-sm text-slate-500">{d.company}</div>
        <div className="text-xl font-semibold">Horizontal Balance Sheet</div>
        <div className="mt-1 text-sm text-slate-600">As of {dateLabel(d.as_of)} · Basis: Accrual</div>
      </div>
      <div className="grid md:grid-cols-2 md:divide-x md:divide-slate-200">
        <TSide title="Liabilities" rows={left} total={total} lp={lp} />
        <TSide title="Assets" rows={right} total={total} lp={lp} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- trial balance / general ledger
function TrialBalance({ report, general }) {
  const [period, setPeriod, fyMonth] = usePeriod("this_fy");
  const params = { date_from: period.from, date_to: period.to };
  const state = useBooks("/books/reports/trial-balance", params);
  const lp = linkFor(period);
  const nav = useNavigate();
  const columns = general ? [
    { key: "name", label: "Account", render: (r) => r.guid ? <span className="text-blue-700">{r.name}</span> : r.name },
    { key: "group", label: "Group", render: (r) => <span className="text-slate-600">{r.group || r.section}</span> },
    { key: "opening", label: "Opening", align: "right", render: (r) => drcr(r.opening) },
    { key: "debit", label: "Debit", align: "right", render: (r) => amount(r.debit) },
    { key: "credit", label: "Credit", align: "right", render: (r) => amount(r.credit) },
    { key: "closing", label: "Balance", align: "right", render: (r) => drcr(r.closing) },
  ] : [
    { key: "name", label: "Account", render: (r) => r.guid ? <span className="text-blue-700">{r.name}</span> : r.name },
    { key: "group", label: "Group", render: (r) => <span className="text-slate-600">{r.group || r.section}</span> },
    { key: "closing_debit", label: "Net debit", align: "right", render: (r) => (r.closing_debit ? amount(r.closing_debit) : "") },
    { key: "closing_credit", label: "Net credit", align: "right", render: (r) => (r.closing_credit ? amount(r.closing_credit) : "") },
  ];
  return (
    <ReportShell report={report} exportPath="/books/reports/trial-balance" exportParams={params}
      filters={<PeriodFilter value={period} onChange={setPeriod} fyMonth={fyMonth} />}>
      <Loaded state={state}>
        {state.data && (
          <>
            <div className="mb-2 text-sm text-slate-600">{state.data.company} · {periodLabel(period)}</div>
            <DataTable columns={columns} rowKey={(r) => r.guid || r.name}
              // Trial balance: ledgers with a balance, as Tally lists them. General ledger: any activity.
              rows={state.data.rows.filter((r) => (general ? r.opening || r.debit || r.credit || r.closing : r.closing))}
              onRowClick={(r) => r.guid && nav(`/accounts/${r.guid}${lp}`)}
              footer={general ? (
                <tr>
                  <td className="px-4 py-2.5" colSpan={3}>Total</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{amount(state.data.movement_debit)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{amount(state.data.movement_credit)}</td>
                  <td />
                </tr>
              ) : (
                <tr>
                  <td className="px-4 py-2.5" colSpan={2}>Total</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{amount(state.data.total_debit)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{amount(state.data.total_credit)}</td>
                </tr>
              )} />
            {Math.abs(state.data.difference) >= 0.01 && (
              <div className="mt-2 text-sm text-amber-800">Difference in opening balances: {amount(state.data.difference)}</div>
            )}
          </>
        )}
      </Loaded>
    </ReportShell>
  );
}

// ---------------------------------------------------------------- account transactions
function AccountTransactions({ report }) {
  const [params, setParams] = useSearchParams();
  const [period, setPeriod, fyMonth] = usePeriod("this_fy");
  const guid = params.get("ledger");
  const coa = useBooks("/books/accounts");
  const [q, setQ] = useState("");
  const ledgers = useMemo(() => {
    const out = [];
    const walk = (n) => { if (n.type === "ledger") out.push(n); (n.children || []).forEach(walk); };
    (coa.data?.groups || []).forEach(walk);
    return out.sort((a, b) => a.name.localeCompare(b.name));
  }, [coa.data]);
  const pick = (g) => { const next = new URLSearchParams(params); next.set("ledger", g); setParams(next, { replace: true }); };
  const current = ledgers.find((l) => l.guid === guid);
  const matches = ledgers.filter((l) => !q.trim() || l.name.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 12);
  return (
    <ReportShell report={report}
      filters={<>
        <div className="relative">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={current ? current.name : "Choose an account"} className="w-72" />
          {q && (
            <div className="absolute left-0 top-full z-30 mt-1 w-72 rounded-md border border-slate-200 bg-white py-1 shadow-lg">
              {matches.map((l) => (
                <button key={l.guid} onClick={() => { pick(l.guid); setQ(""); }} className="block w-full px-3 py-1.5 text-left text-sm hover:bg-slate-50">{l.name}</button>
              ))}
              {!matches.length && <div className="px-3 py-1.5 text-sm text-slate-500">No account matches</div>}
            </div>
          )}
        </div>
        {current && <span className="text-sm font-medium">{current.name}</span>}
      </>}>
      {guid ? (
        <LedgerStatement guid={guid} period={period} setPeriod={setPeriod} fyMonth={fyMonth} />
      ) : (
        <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">Choose an account to see its transactions.</div>
      )}
    </ReportShell>
  );
}

// ---------------------------------------------------------------- day book
function DayBook({ report }) {
  const [period, setPeriod, fyMonth] = usePeriod("today");
  const [page, setPage] = useState(1);
  const params = { date_from: period.from, date_to: period.to };
  const state = useBooks("/books/reports/day-book", { ...params, page });
  const nav = useNavigate();
  return (
    <ReportShell report={report} exportPath="/books/reports/day-book" exportParams={params}
      filters={<PeriodFilter value={period} onChange={(p) => { setPeriod(p); setPage(1); }} fyMonth={fyMonth} />}>
      <Loaded state={state}>
        {state.data && (
          <>
            <DataTable rows={state.data.rows} onRowClick={(v) => nav(`/vouchers/${v.guid}`)} empty="No vouchers on these dates."
              columns={[
                { key: "date", label: "Date", render: (v) => dateLabel(v.date), className: "whitespace-nowrap" },
                { key: "party", label: "Particulars", render: (v) => v.party || v.narration || "—" },
                { key: "voucher_type", label: "Vch type" },
                { key: "number", label: "Vch no.", render: (v) => <span className="text-blue-700">{v.number || "—"}</span> },
                { key: "amount", label: "Amount", align: "right", render: (v) => (v.is_cancelled ? <span className="text-xs text-rose-600">Cancelled</span> : amount(v.amount)) },
              ]} />
            <Pager page={state.data.page} pageSize={state.data.page_size} total={state.data.total} onChange={setPage} />
          </>
        )}
      </Loaded>
    </ReportShell>
  );
}

// ---------------------------------------------------------------- ageing & balances
function Ageing({ report, kind, detail }) {
  const [asOf, setAsOf] = useAsOf();
  const params = { kind, as_of: asOf, ...(detail ? { detail: true } : {}) };
  const state = useBooks("/books/reports/ageing", { kind, as_of: asOf });
  const path = kind === "customer" ? "/customers" : "/vendors";
  const nav = useNavigate();
  return (
    <ReportShell report={report} exportPath="/books/reports/ageing" exportParams={params}
      filters={<><AsOfFilter value={asOf} onChange={setAsOf} /><span className="text-xs text-slate-500">Aging by due date</span></>}>
      <Loaded state={state}>
        {state.data && (detail ? (
          <DataTable rows={state.data.bills} rowKey={(b) => `${b.party_guid}-${b.bill}`}
            onRowClick={(b) => b.voucher_guid && nav(`/vouchers/${b.voucher_guid}`)} empty="Nothing outstanding."
            columns={[
              { key: "party", label: kind === "customer" ? "Customer" : "Vendor" },
              { key: "bill", label: "Bill", render: (b) => <span className="text-blue-700">{b.bill}</span> },
              { key: "date", label: "Date", render: (b) => dateLabel(b.date) },
              { key: "due_date", label: "Due date", render: (b) => dateLabel(b.due_date) },
              { key: "age", label: "Age", render: (b) => (b.days_overdue ? `${b.days_overdue} days` : "Current") },
              { key: "outstanding", label: "Balance due", align: "right", render: (b) => money(b.outstanding) },
            ]} />
        ) : (
          <DataTable rows={state.data.rows} onRowClick={(r) => nav(`${path}/${r.guid}`)} empty="Nothing outstanding."
            columns={[
              { key: "name", label: kind === "customer" ? "Customer name" : "Vendor name", render: (r) => <span className="text-blue-700">{r.name}</span> },
              ...state.data.buckets.map((b) => ({ key: b.key, label: b.label, align: "right", render: (r) => (r[b.key] ? amount(r[b.key]) : "—") })),
              { key: "total", label: "Total", align: "right", render: (r) => <span className="font-medium">{amount(r.total)}</span> },
            ]}
            footer={(
              <tr>
                <td className="px-4 py-2.5">Total</td>
                {state.data.buckets.map((b) => <td key={b.key} className="px-4 py-2.5 text-right tabular-nums">{amount(state.data.totals[b.key])}</td>)}
                <td className="px-4 py-2.5 text-right tabular-nums">{amount(state.data.totals.total)}</td>
              </tr>
            )} />
        ))}
      </Loaded>
    </ReportShell>
  );
}

function PartyBalances({ report, kind }) {
  const [asOf, setAsOf] = useAsOf();
  const state = useBooks("/books/reports/party-balances", { kind, as_of: asOf });
  const nav = useNavigate();
  const path = kind === "customer" ? "/customers" : "/vendors";
  return (
    <ReportShell report={report} exportPath="/books/reports/party-balances" exportParams={{ kind, as_of: asOf }}
      filters={<AsOfFilter value={asOf} onChange={setAsOf} />}>
      <Loaded state={state}>
        {state.data && (
          <DataTable rows={state.data.rows} onRowClick={(r) => nav(`${path}/${r.guid}`)} empty="No balances."
            columns={[
              { key: "name", label: kind === "customer" ? "Customer name" : "Vendor name", render: (r) => <span className="text-blue-700">{r.name}</span> },
              { key: "balance", label: "Balance", align: "right", render: (r) => money(r.balance) },
            ]}
            footer={<tr><td className="px-4 py-2.5">Total</td><td className="px-4 py-2.5 text-right tabular-nums">{money(state.data.total)}</td></tr>} />
        )}
      </Loaded>
    </ReportShell>
  );
}

// ---------------------------------------------------------------- sales / purchases / stock
function Summary({ report }) {
  const [period, setPeriod, fyMonth] = usePeriod("this_fy");
  const params = { date_from: period.from, date_to: period.to };
  const state = useBooks(`/books/reports/${report.key}`, params);
  const byItem = report.key.endsWith("item");
  const nav = useNavigate();
  const partyPath = report.key.startsWith("sales") ? "/customers" : "/vendors";
  return (
    <ReportShell report={report} exportPath={`/books/reports/${report.key}`} exportParams={params}
      filters={<PeriodFilter value={period} onChange={setPeriod} fyMonth={fyMonth} />}>
      <Loaded state={state}>
        {state.data && (
          <DataTable rows={state.data.rows} rowKey={(r) => r.guid || r.name}
            onRowClick={!byItem ? (r) => r.guid && nav(`${partyPath}/${r.guid}`) : undefined}
            columns={byItem ? [
              { key: "name", label: "Item name" },
              { key: "qty", label: "Quantity", align: "right", render: (r) => `${r.qty} ${r.unit || ""}` },
              { key: "amount", label: "Amount", align: "right", render: (r) => money(r.amount) },
              { key: "average_rate", label: "Average price", align: "right", render: (r) => money(r.average_rate) },
            ] : [
              { key: "name", label: report.key.startsWith("sales") ? "Customer name" : "Vendor name", render: (r) => <span className={r.guid ? "text-blue-700" : ""}>{r.name}</span> },
              { key: "count", label: report.key.startsWith("sales") ? "Invoice count" : "Bill count", align: "right" },
              { key: "amount", label: report.key.startsWith("sales") ? "Sales" : "Purchases", align: "right", render: (r) => money(r.amount) },
              { key: "total", label: "With tax", align: "right", render: (r) => money(r.total) },
            ]}
            footer={<tr><td className="px-4 py-2.5" colSpan={2}>Total</td><td className="px-4 py-2.5 text-right tabular-nums">{money(state.data.total)}</td>
              {byItem ? <td /> : <td className="px-4 py-2.5 text-right tabular-nums">{money(state.data.rows.reduce((t, r) => t + (r.total || 0), 0))}</td>}</tr>} />
        )}
      </Loaded>
    </ReportShell>
  );
}

function StockSummary({ report }) {
  const [asOf, setAsOf] = useAsOf();
  const state = useBooks("/books/reports/stock-summary", { as_of: asOf });
  const nav = useNavigate();
  return (
    <ReportShell report={report} exportPath="/books/reports/stock-summary" exportParams={{ as_of: asOf }}
      filters={<><AsOfFilter value={asOf} onChange={setAsOf} />
        {state.data?.snapshot?.as_of && <span className="text-xs text-slate-500">Values as Tally computed them on {dateLabel(state.data.snapshot.as_of)}</span>}</>}>
      <Loaded state={state}>
        {state.data && (
          <DataTable rows={state.data.rows} onRowClick={(r) => nav(`/items/${r.guid}`)} empty="No stock items."
            columns={[
              { key: "name", label: "Item", render: (r) => <span className="text-blue-700">{r.name}</span> },
              { key: "group", label: "Stock group", render: (r) => r.group || "—" },
              { key: "qty", label: "Quantity", align: "right", render: (r) => `${r.qty} ${r.unit || ""}` },
              { key: "rate", label: "Rate", align: "right", render: (r) => amount(r.rate) },
              { key: "value", label: "Value", align: "right", render: (r) => money(r.value) },
            ]}
            footer={<tr><td className="px-4 py-2.5" colSpan={4}>Total</td><td className="px-4 py-2.5 text-right tabular-nums">{money(state.data.total)}</td></tr>} />
        )}
      </Loaded>
    </ReportShell>
  );
}

// ---------------------------------------------------------------- router
export default function ReportView() {
  const { key } = useParams();
  const report = REPORTS.find((r) => r.key === key);
  if (!report) {
    return <div className="text-sm text-slate-500">Unknown report. <Link to="/reports" className="text-blue-700">All reports</Link></div>;
  }
  switch (key) {
    case "profit-and-loss": return <ProfitAndLoss report={report} />;
    case "horizontal-profit-and-loss": return <ProfitAndLoss report={report} horizontal />;
    case "balance-sheet": return <BalanceSheet report={report} />;
    case "horizontal-balance-sheet": return <BalanceSheet report={report} horizontal />;
    case "trial-balance": return <TrialBalance report={report} />;
    case "general-ledger": return <TrialBalance report={report} general />;
    case "account-transactions": return <AccountTransactions report={report} />;
    case "day-book": return <DayBook report={report} />;
    case "ar-aging-summary": return <Ageing report={report} kind="customer" />;
    case "ar-aging-details": return <Ageing report={report} kind="customer" detail />;
    case "ap-aging-summary": return <Ageing report={report} kind="vendor" />;
    case "ap-aging-details": return <Ageing report={report} kind="vendor" detail />;
    case "customer-balance-summary": return <PartyBalances report={report} kind="customer" />;
    case "vendor-balance-summary": return <PartyBalances report={report} kind="vendor" />;
    case "stock-summary": return <StockSummary report={report} />;
    default: return <Summary report={report} />;
  }
}
